import { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { toast } from 'react-toastify';
import { 
  FaTimes, 
  FaBox, 
  FaShieldAlt, 
  FaHistory, 
  FaExclamationTriangle, 
  FaCheckCircle,
  FaClock,
  FaFileAlt,
  FaCalendarAlt,
  FaTruck,
  FaTools
} from 'react-icons/fa';

import { MaterialTrackerItem, getProjectMaterial } from '../../api/materialTracker';
import { getProjectWarranties, WarrantyInterface } from '../../api/warranties';
import { DesignSystem } from '../../styles/designSystem';

interface MaterialLifecycleModalProps {
  projectId: string;
  materialId: string;
  isOpen: boolean;
  onClose: () => void;
  material?: MaterialTrackerItem;
}

interface LifecycleEvent {
  id: string;
  title: string;
  description: string;
  date: string;
  type: 'creation' | 'status_change' | 'warranty' | 'delivery' | 'installation' | 'issue';
  icon: React.ReactNode;
  color: string;
}

export const MaterialLifecycleModal = ({ 
  projectId, 
  materialId, 
  isOpen, 
  onClose, 
  material: initialMaterial 
}: MaterialLifecycleModalProps) => {
  const { keycloak } = useKeycloak();
  const [material, setMaterial] = useState<MaterialTrackerItem | null>(initialMaterial || null);
  const [warranties, setWarranties] = useState<WarrantyInterface[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'timeline' | 'warranties' | 'documents'>('overview');

  // Load material data
  useEffect(() => {
    if (isOpen && materialId && keycloak.token) {
      loadMaterialData();
    }
  }, [isOpen, materialId, keycloak.token]);

  const loadMaterialData = async () => {
    if (!keycloak.token) return;

    setIsLoading(true);
    try {
      // Load material details if not provided
      if (!material) {
        const materialData = await getProjectMaterial(keycloak.token, projectId, materialId);
        setMaterial(materialData);
      }

      // Load related warranties
      const warrantyResponse = await getProjectWarranties(keycloak.token, projectId, 1, 100);
      const relatedWarranties = warrantyResponse.results.filter(w => w.project_material_id === materialId);
      setWarranties(relatedWarranties);

    } catch (error) {
      console.error('Error loading material lifecycle data:', error);
      toast.error('Failed to load material lifecycle data');
    } finally {
      setIsLoading(false);
    }
  };

  // Generate lifecycle timeline
  const generateLifecycleEvents = (): LifecycleEvent[] => {
    const events: LifecycleEvent[] = [];

    if (material) {
      // Material creation
      events.push({
        id: 'created',
        title: 'Material Created',
        description: `Material "${material.name}" added to project`,
        date: material.created_at || new Date().toISOString(),
        type: 'creation',
        icon: <FaBox className="w-4 h-4" />,
        color: DesignSystem.colors.brand[600]
      });

      // Order date
      if (material.ordered_at) {
        events.push({
          id: 'ordered',
          title: 'Material Ordered',
          description: material.tracking_number ? `Tracking: ${material.tracking_number}` : 'Order placed',
          date: material.ordered_at,
          type: 'status_change',
          icon: <FaTruck className="w-4 h-4" />,
          color: DesignSystem.colors.status.inProgress
        });
      }

      // Expected delivery
      if (material.eta) {
        const isOverdue = new Date(material.eta) < new Date();
        events.push({
          id: 'eta',
          title: isOverdue ? 'Delivery Overdue' : 'Expected Delivery',
          description: `ETA: ${new Date(material.eta).toLocaleDateString()}`,
          date: material.eta,
          type: isOverdue ? 'issue' : 'delivery',
          icon: isOverdue ? <FaExclamationTriangle className="w-4 h-4" /> : <FaTruck className="w-4 h-4" />,
          color: isOverdue ? DesignSystem.colors.risk.high : DesignSystem.colors.status.notStarted
        });
      }

      // Installation date
      if (material.isd) {
        events.push({
          id: 'installed',
          title: 'Material Installed',
          description: `Installation completed on ${new Date(material.isd).toLocaleDateString()}`,
          date: material.isd,
          type: 'installation',
          icon: <FaTools className="w-4 h-4" />,
          color: DesignSystem.colors.status.completed
        });
      }
    }

    // Add warranty events
    warranties.forEach(warranty => {
      events.push({
        id: `warranty-${warranty.id}`,
        title: 'Warranty Active',
        description: `${warranty.warranty_provider} warranty (${warranty.days_remaining} days remaining)`,
        date: warranty.warranty_start_date,
        type: 'warranty',
        icon: <FaShieldAlt className="w-4 h-4" />,
        color: warranty.is_active_warranty ? DesignSystem.colors.status.completed : DesignSystem.colors.status.notStarted
      });
    });

    // Sort events by date
    return events.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'ordered':
        return DesignSystem.colors.status.inProgress;
      case 'delivered':
        return DesignSystem.colors.status.completed;
      case 'pending':
        return DesignSystem.colors.status.notStarted;
      case 'delayed':
        return DesignSystem.colors.risk.high;
      default:
        return DesignSystem.colors.primary.textMuted;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'ordered':
        return <FaTruck className="w-4 h-4" />;
      case 'delivered':
        return <FaCheckCircle className="w-4 h-4" />;
      case 'pending':
        return <FaClock className="w-4 h-4" />;
      case 'delayed':
        return <FaExclamationTriangle className="w-4 h-4" />;
      default:
        return <FaBox className="w-4 h-4" />;
    }
  };

  if (!isOpen) return null;

  const lifecycleEvents = generateLifecycleEvents();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] w-full max-w-5xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#eaeaea]">
          <div className="flex items-center">
            <FaHistory className="w-6 h-6 text-[#171717] mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-[#171717]">Material Lifecycle</h2>
              <p className="text-sm text-[#888]">
                {material?.name || 'Loading...'} • ID: {materialId}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[#fafafa] rounded-full transition-colors"
          >
            <FaTimes className="w-5 h-5 text-[#888]" />
          </button>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#171717] mx-auto"></div>
            <p className="mt-4 text-[#555]">Loading material lifecycle data...</p>
          </div>
        )}

        {/* Content */}
        {!isLoading && material && (
          <>
            {/* Tabs */}
            <div className="border-b border-[#eaeaea]">
              <nav className="-mb-px flex space-x-8 px-6">
                {[
                  { id: 'overview', label: 'Overview', icon: <FaBox className="w-4 h-4" /> },
                  { id: 'timeline', label: 'Timeline', icon: <FaHistory className="w-4 h-4" /> },
                  { id: 'warranties', label: 'Warranties', icon: <FaShieldAlt className="w-4 h-4" /> },
                  { id: 'documents', label: 'Documents', icon: <FaFileAlt className="w-4 h-4" /> }
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                      activeTab === tab.id
                        ? 'border-[#888] text-[#171717]'
                        : 'border-transparent text-[#888] hover:text-[#444] hover:border-[#eaeaea]'
                    }`}
                  >
                    {tab.icon}
                    <span>{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  
                  {/* Material Summary */}
                  <div className="bg-[#fafafa] rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-[#171717]">Material Summary</h3>
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(material.status || 'pending')}
                        <span 
                          className="px-3 py-1 rounded-full text-sm font-medium"
                          style={{
                            backgroundColor: getStatusColor(material.status || 'pending') + '20',
                            color: getStatusColor(material.status || 'pending')
                          }}
                        >
                          {material.status || 'Pending'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-[#555]">Name</p>
                        <p className="font-medium text-[#171717]">{material.name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-[#555]">Item/SKU</p>
                        <p className="font-medium text-[#171717]">{material.item}</p>
                      </div>
                      <div>
                        <p className="text-sm text-[#555]">Quantity</p>
                        <p className="font-medium text-[#171717]">{material.quantity}</p>
                      </div>
                      <div>
                        <p className="text-sm text-[#555]">Tracking Number</p>
                        <p className="font-medium text-[#171717]">{material.tracking_number || 'Not assigned'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-[#f5f5f5] rounded-lg p-4">
                      <div className="flex items-center">
                        <FaShieldAlt className="w-5 h-5 text-[#171717] mr-2" />
                        <span className="text-sm text-[#171717]">Warranties</span>
                      </div>
                      <p className="text-2xl font-semibold text-[#171717] mt-1">{warranties.length}</p>
                    </div>
                    
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="flex items-center">
                        <FaCheckCircle className="w-5 h-5 text-green-600 mr-2" />
                        <span className="text-sm text-green-600">Status</span>
                      </div>
                      <p className="text-sm font-medium text-green-900 mt-1">
                        {material.status === 'delivered' ? 'Delivered' : material.status === 'ordered' ? 'In Transit' : 'Pending'}
                      </p>
                    </div>
                    
                    <div className="bg-orange-50 rounded-lg p-4">
                      <div className="flex items-center">
                        <FaCalendarAlt className="w-5 h-5 text-orange-600 mr-2" />
                        <span className="text-sm text-orange-600">ETA</span>
                      </div>
                      <p className="text-sm font-medium text-orange-900 mt-1">
                        {material.eta ? new Date(material.eta).toLocaleDateString() : 'Not set'}
                      </p>
                    </div>
                  </div>

                  {/* Description */}
                  {material.description && (
                    <div>
                      <h4 className="text-sm font-medium text-[#171717] mb-2">Description</h4>
                      <p className="text-[#444] text-sm bg-[#fafafa] p-4 rounded-lg">{material.description}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Timeline Tab */}
              {activeTab === 'timeline' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-[#171717]">Material Timeline</h3>
                  
                  {lifecycleEvents.length === 0 ? (
                    <div className="text-center py-8 text-[#888]">
                      <FaHistory className="w-8 h-8 mx-auto mb-2 text-[#aaa]" />
                      <p>No timeline events found</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {lifecycleEvents.map((event) => (
                        <div key={event.id} className="flex items-start space-x-4">
                          <div 
                            className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white"
                            style={{ backgroundColor: event.color }}
                          >
                            {event.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <h4 className="text-sm font-medium text-[#171717]">{event.title}</h4>
                              <span className="text-xs text-[#888]">
                                {new Date(event.date).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-sm text-[#555] mt-1">{event.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Warranties Tab */}
              {activeTab === 'warranties' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-[#171717]">Related Warranties</h3>
                  
                  {warranties.length === 0 ? (
                    <div className="text-center py-8 text-[#888]">
                      <FaShieldAlt className="w-8 h-8 mx-auto mb-2 text-[#aaa]" />
                      <p>No warranties found for this material</p>
                      <button className="mt-4 text-[#171717] hover:text-[#171717] text-sm font-medium">
                        Create Warranty
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {warranties.map(warranty => (
                        <div key={warranty.id} className="border border-[#eaeaea] rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-[#171717]">{warranty.warranty_provider}</h4>
                            <span 
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                warranty.is_active_warranty 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {warranty.is_active_warranty ? 'Active' : 'Expired'}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-[#555]">Serial Number</p>
                              <p className="font-medium">{warranty.serial_number}</p>
                            </div>
                            <div>
                              <p className="text-[#555]">Model</p>
                              <p className="font-medium">{warranty.model}</p>
                            </div>
                            <div>
                              <p className="text-[#555]">Start Date</p>
                              <p className="font-medium">{new Date(warranty.warranty_start_date).toLocaleDateString()}</p>
                            </div>
                            <div>
                              <p className="text-[#555]">End Date</p>
                              <p className="font-medium">{new Date(warranty.warranty_end_date).toLocaleDateString()}</p>
                            </div>
                          </div>
                          {warranty.is_active_warranty && (
                            <div className="mt-3 flex items-center text-sm text-green-600">
                              <FaCheckCircle className="w-4 h-4 mr-1" />
                              {warranty.days_remaining} days remaining
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Documents Tab */}
              {activeTab === 'documents' && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-[#171717]">Related Documents</h3>
                  
                  <div className="text-center py-8 text-[#888]">
                    <FaFileAlt className="w-8 h-8 mx-auto mb-2 text-[#aaa]" />
                    <p>Document integration coming soon</p>
                    <p className="text-sm">This will show material-related documents and specifications</p>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}; 